import React, { useCallback } from 'react';
import { styled } from '@mui/material/styles';
import { Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { renderTimeViewClock } from '@mui/x-date-pickers/timeViewRenderers';
import dayjs from 'dayjs';
import { NODE_UI, stopPropagation } from './nodeUiStyles';

/**
 * NodeDateTimePicker — Combined date/time picker using MUI X.
 * Used by: Session Variables (DateTime type), Query DB (datetime column conditions).
 */

const Label = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.label,
  fontWeight: NODE_UI.fontWeight.label,
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
  marginBottom: '4px',
  userSelect: 'none',
}));

const PickerWrapper = styled('div')(({ theme }) => ({
  width: '100%',
  overflow: 'visible',
  '& .MuiOutlinedInput-root': {
    fontSize: NODE_UI.fontSize.value,
    borderRadius: NODE_UI.borderRadiusSm,
    backgroundColor: theme.palette.background.default,
    minHeight: '36px',
    boxSizing: 'border-box',
    '& fieldset': { borderColor: theme.palette.divider },
    '&:hover fieldset': { borderColor: theme.palette.text.secondary },
    '&.Mui-focused fieldset': { borderColor: theme.palette.text.primary, borderWidth: '1px' },
  },
  '& .MuiInputBase-input': {
    padding: NODE_UI.input.padding,
    fontSize: NODE_UI.fontSize.value,
    lineHeight: 1.5,
  },
  '& .MuiInputAdornment-root': {
    marginLeft: 0,
  },
  '& .MuiIconButton-root': {
    padding: '4px',
  },
}));

const NodeDateTimePicker = React.memo(function NodeDateTimePicker({
  label,
  value,
  onChange,
  disabled = false,
  timezone = 'UTC',
  format = 'YYYY/MM/DD hh:mm a',
  placeholder = 'Pick a date and time',
  sx,
}) {
  const handleChange = useCallback((newValue) => {
    try {
      onChange?.(newValue ? newValue.toISOString() : '');
    } catch {
      onChange?.(newValue ? newValue.toString() : '');
    }
  }, [onChange]);

  const dayjsValue = value ? dayjs(value) : null;

  return (
    <div onMouseDown={stopPropagation} onClick={stopPropagation} onPointerDown={stopPropagation}>
      {label && <Label>{label}</Label>}
      <PickerWrapper sx={sx}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DateTimePicker
            value={dayjsValue}
            onChange={handleChange}
            disabled={disabled}
            timezone={timezone}
            format={format}
            viewRenderers={{
              hours: renderTimeViewClock,
              minutes: renderTimeViewClock,
              seconds: renderTimeViewClock,
            }}
            slotProps={{
              textField: {
                size: 'small',
                variant: 'outlined',
                fullWidth: true,
                placeholder,
              },
            }}
          />
        </LocalizationProvider>
      </PickerWrapper>
    </div>
  );
});

export default NodeDateTimePicker;
